// import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Filters from "./components_1/Filters";
import { fetchLeaderboard } from "./services_1/api";
import "./App.css";
import React, { useMemo, useState, useRef, useEffect } from "react";
// ... rest of your imports

import SprintTrendChart from "./components_1/sprint_tree";

import DeveloperPieChart from "./components_1/DeveloperPieChart";


const Tooltip = ({ text, children, position = "top" }) => {
  const [visible, setVisible] = useState(false);

  return (
    <div 
      className="custom-tooltip-container"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      {children}
      {visible && text && (
        <div className={`custom-tooltip-box position-${position}`}>
          {text}
          <div className="custom-tooltip-arrow" />
        </div>
      )}
    </div>
  );
};

const parseDate = (value) => {
  if (value === undefined || value === null || value === "" || value === "0" || value === -1 || value === '-1' ) return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    return new Date(value < 1e12 ? value * 1000 : value);
  }
  const numericValue = Number(value);
  if (Number.isFinite(numericValue) && numericValue !== 0 ){
    return new Date(numericValue < 1e12 ? numericValue * 1000 : numericValue);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value) => {
  const date = parseDate(value);
  if (!date) return "Not available";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

const sumValues = (obj = {}) =>
  Object.values(obj).reduce((total, value) => total + (Number(value) || 0), 0);

const getCompletionPercent = (burndownData) => {
  const total = Number(burndownData?.totalEstimationPoints) || 0;
  if (!total) return 0;
  return Math.min(100, Math.round((sumValues(burndownData.completedPointsByDate) / total) * 100));
};

const getExpectedProgress = (startDate, endDate) => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  if (!start || !end) return null;

  const today = new Date();
  if (today <= start) return 0;
  if (today >= end) return 100;

  const total = end.getTime() - start.getTime();
  const elapsed = today.getTime() - start.getTime();
  return Math.round((elapsed / total) * 100);
};

const getProjectStatus = (completionPercent, startDate, endDate) => {
  if (completionPercent >= 100) return "Completed";

  const expectedProgress = getExpectedProgress(startDate, endDate);
  const end = parseDate(endDate);

  if (end && new Date() > end) return "Late";
  if (expectedProgress !== null && completionPercent + 10 < expectedProgress) return "At Risk";
  return "On Track";
};


const getMilestones = (projectBurndownData = {}) => {
  // 1. Safely extract the modules array from the backend payload
  const modules = projectBurndownData.modules || [];

  // 2. Map over the modules instead of the sprints
  return modules
    .map((mod, index) => {
      // For now, we use placeholders for the dates and status 
      // since they aren't available in the description yet.
      return {
        name: mod.moduleName,       // ✅ Replaces "Sprint 1" with "Initial Setup"
        phaseNo: mod.phaseNo,       // Handy to keep for reference
        status: mod.status || "Unknown",          // 🚧 Placeholder: Update when description has status
        finishDate: mod.finishDate || "-",            // 🚧 Placeholder
        baselineFinish:mod.baselineFinish || "-",        // 🚧 Placeholder
        daysLate: mod.daysLate || 0,                // 🚧 Placeholder
        
        // Use the Phase Number to ensure they stay in the exact correct order
        sortDate: parseInt(mod.phaseNo, 10) || index 
      };
    })
    .sort((a, b) => a.sortDate - b.sortDate)
  // Keeps your original logic of only showing the first 4 (Upcoming)
};

const getSprintStats = (selectedSprint, sprintBurndownData) => {
  const sprintEntries = Object.entries(sprintBurndownData || {});

  // =========================================================
  // 📈 PART 1: OVERALL PROJECT PROGRESS (Always process ALL sprints)
  // =========================================================
  let projectTotalPoints = 0;
  let projectCompletedPoints = 0;

  sprintEntries.forEach(([, sprint]) => {
    projectTotalPoints += sprint.totalEstimationPoints || 0;
    projectCompletedPoints += sprint.completedPoints || 0;
  });

  // Calculate global project completion percentage
  const projectCompletionPercentage = projectTotalPoints 
    ? Math.min(100, Math.round((projectCompletedPoints / projectTotalPoints) * 100)) 
    : 0;

  // =========================================================
  // 🔍 PART 2: SPRINT FILTRATION LOGIC (Your original logic)
  // =========================================================
  const selectedEntries =
    selectedSprint === "all"
      ? sprintEntries
      : sprintEntries.filter(([name]) => name === selectedSprint);

  let totalEstimatedHours = 0; // renamed from totalHours for clarity
  let totalLoggedHours = 0;    // 🔥 NEW: Track actual logged hours
  let completedItems = 0;
  let assignedItems = 0;
  let completedPoints = 0;
  let totalPoints = 0;
  let devVelocity = 0;
  let deliveryVelocity = 0;

  selectedEntries.forEach(([, sprint]) => {
    totalEstimatedHours += sprint.totalHours || 0; 
    totalLoggedHours += sprint.totalLoggedHours || 0; // 🔥 NEW: Grab logged hours
    completedItems += sprint.completedItems || 0;
    assignedItems += sprint.totalItems || 0;
    completedPoints += sprint.completedPoints || 0;
    totalPoints += sprint.totalEstimationPoints || 0;
    devVelocity += sprint.Developer_vel || 0;
    deliveryVelocity += sprint.Delivery_vel || 0;
  });

  const taskVariance = assignedItems ? Math.min(100, Math.round(((assignedItems - completedItems) / assignedItems) * 100)) : 0;
  const pointVariance = totalPoints ? Math.min(100, Math.round(((totalPoints - devVelocity) / totalPoints) * 100)) : 0;

  // 🔥 NEW: Extract the description for the currently selected sprint
  let description = "";
  if (selectedSprint !== "all" && sprintBurndownData && sprintBurndownData[selectedSprint]) {
    description = sprintBurndownData[selectedSprint].description || "";
  }

  return {
    totalEstimatedHours,
    totalLoggedHours, // 🔥 NEW: Return it for the UI
    completedItems,
    assignedItems,
    completedPoints,
    totalPoints,
    taskVariance,
    pointVariance,
    projectCompletionPercentage,
    devVelocity,
    deliveryVelocity, // 🔥 NEW: Attach overall project completion for the summary box
    description // 🔥 NEW: Return the description for the UI
  };
};

// Define your expected stages here. You can add more mapping keys easily.
const EXPECTED_STAGES = [
  { key: "prod", label: "Production" },
  { key: "uat", label: "UAT" },
  { key: "qa", label: "QA / Testing" },
  { key: "dev", label: "Development" },
  { key: "stag", label: "Staging" },
  { key: "Integration", label: "Integration Level" },
  { key: "Staging", label: "Staging Env" },
];

const EXPECTED_STAGES_o = [
  { key: "unit", label: "Unit Level" },
  { key: "integration", label: "Integration Level" }
];

const getDefectPhaseData = (items = []) => {
  const countsMap = {};

  items.forEach(item => {
    // 🔥 UPDATED: Removed the item.type check, since we will only pass bugs into this function now
    if (Array.isArray(item.tags) && item.tags.length > 0) {
      
      let matchedLabel = "Other / Uncategorized";

      const combinedTags = item.tags
        .map(tag => (typeof tag === "string" ? tag : tag.name || tag.value || ""))
        .join(" ")
        .toLowerCase();
      
      for (let stage of EXPECTED_STAGES) {
        if (combinedTags.includes(stage.key)) {
          matchedLabel = stage.label;
          break;
        }
      }

      countsMap[matchedLabel] = (countsMap[matchedLabel] || 0) + 1;
    }
  });

  return Object.entries(countsMap)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
};

const getDefectPhaseData_o = (items = []) => {
  const countsMap = {};

  items.forEach(item => {
    // 🔥 UPDATED: Removed the item.type check, since we will only pass bugs into this function now
    if (Array.isArray(item.tags) && item.tags.length > 0) {
      
      let matchedLabel = "Other / Uncategorized";

      const combinedTags = item.tags
        .map(tag => (typeof tag === "string" ? tag : tag.name || tag.value || ""))
        .join(" ")
        .toLowerCase();
      
      for (let stage of EXPECTED_STAGES_o) {
        if (combinedTags.includes(stage.key)) {
          matchedLabel = stage.label;
          break;
        }
      }

      countsMap[matchedLabel] = (countsMap[matchedLabel] || 0) + 1;
    }
  });

  return Object.entries(countsMap)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
};


// --- NEW HELPER: EXTRACT DEVELOPER LEADERBOARD ---
const getDeveloperLeaderboard = (selectedSprint, sprintBurndownData) => {
  const devMap = {};
  const sprintsToProcess = selectedSprint === "all"
    ? Object.values(sprintBurndownData || {})
    : [sprintBurndownData?.[selectedSprint]].filter(Boolean);

  sprintsToProcess.forEach(sprint => {
    if (sprint && sprint.developerDistribution) {
      Object.entries(sprint.developerDistribution).forEach(([devName, stats]) => {
        // Group unknown/external items into a single "Unassigned" bucket
        const normalizedName = (devName === "Unknown/External") ? "Unassigned" : devName;

        if (!devMap[normalizedName]) {
          devMap[normalizedName] = { name: normalizedName, assigned: 0, completed: 0, moved: 0};
        }
        devMap[normalizedName].assigned += (stats.assigned || 0);
        devMap[normalizedName].completed += (stats.completed || 0);
        devMap[normalizedName].moved += (stats.moved || 0);
      });
    }
  });

  return Object.values(devMap)
    // 🔥 FIX: Filter out the "Unassigned" row if it has absolutely zero activity
    .filter(dev => !(dev.name === "Unassigned" && dev.assigned === 0 && dev.completed === 0 && dev.moved === 0))
    // Sort by highest workload, but ALWAYS push "Unassigned" to the very bottom
    .sort((a, b) => {
      if (a.name === "Unassigned") return 1;
      if (b.name === "Unassigned") return -1;
      return b.assigned - a.assigned;
    });
};

// --- CUSTOM SPRINT DROPDOWN ---
const SearchableSprintDropdown = ({ sprintNames, selectedSprint, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);

  // Sync the input text with the selected sprint when closed
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm(selectedSprint === "all" ? "" : selectedSprint);
    }
  }, [selectedSprint, isOpen]);

  // Handle clicking outside to close the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter options based on what the user types
  const filteredSprints = sprintNames.filter((name) =>
    name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%", maxWidth: "250px" }}>
      <input
        className="sprint-search-input"
        type="text"
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setIsOpen(true); // Open menu when typing
        }}
        onFocus={() => setIsOpen(true)}
        placeholder="Type to search sprints..."
        style={{
          width: "100%",
          padding: "8px 12px",
          boxSizing: "border-box",
          borderRadius: "6px",
          border: "1px solid #ccc",
          fontSize: "14px"
        }}
      />
      
      {isOpen && (
        <ul className="custom-sprint-dropdown-menu">
          {/* Always provide the 'All' option at the top */}
          <li
            className="custom-sprint-dropdown-item"
            style={{ fontWeight: "bold" }}
            onMouseDown={() => {
              onSelect("all");
              setIsOpen(false);
            }}
          >
            All Sprints
          </li>

          {/* Render the filtered sprints */}
          {filteredSprints.length > 0 ? (
            filteredSprints.map((name) => (
              <li
                key={name}
                className="custom-sprint-dropdown-item"
                onMouseDown={() => {
                  onSelect(name);
                  setIsOpen(false);
                }}
              >
                {name}
              </li>
            ))
          ) : (
            <li className="custom-sprint-dropdown-empty">
              No sprints found
            </li>
          )}
        </ul>
      )}
    </div>
  );
};



function ProjectSnapshot() {
  const navigate = useNavigate();
  const [projectData, setProjectData] = useState(null);
  const [projectName, setProjectName] = useState("ABC Project");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSprint, setSelectedSprint] = useState("all");

  // 🔥 NEW: State to control the popup visibility
  const [isDescModalOpen, setIsDescModalOpen] = useState(false);

  const handleSelect = async (projectId, project) => {
    setIsLoading(true);
    setProjectName(project?.name || "Selected Project");
    setSelectedSprint("all");

    try {
      const result = await fetchLeaderboard(projectId,false);
      setProjectData(result);
    } catch (error) {
      console.error(error);
      setProjectData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const itemDetails = useMemo(
    () => (Array.isArray(projectData?.itemDetails) ? projectData.itemDetails : []),
    [projectData]
  );

  const sprintBurndownData = projectData?.sprintBurndownData || {};
  const projectBurndownData = projectData?.projectBurndownData || {};

  const completionPercent = getCompletionPercent(projectBurndownData);
  const projectStatus = projectBurndownData.status || getProjectStatus(completionPercent, projectBurndownData.startDate, projectBurndownData.endDate);
  const milestones = getMilestones(projectBurndownData);
  const sprintNames = Object.keys(sprintBurndownData);
  const sprintStats = getSprintStats(selectedSprint, sprintBurndownData);

  // Leaderboard Extraction
  const devLeaderboard = useMemo(() => {
    return getDeveloperLeaderboard(selectedSprint, sprintBurndownData);
  }, [selectedSprint, sprintBurndownData]);

  // Helper arrays for avatars and ranks matching your screenshot
  const rankColors = ["#38bdf8", "#94a3b8", "#fb923c"]; // 1st Cyan, 2nd Slate, 3rd Orange
  const avatarColors = ["#0ea5e9", "#06b6d4", "#14b8a6", "#6366f1", "#8b5cf6", "#d946ef", "#f43f5e"];

  // Helper to get relative subtext for the leaderboard based on their rank
  const getSubtext = (index, total) => {
    if (index === 0) return "Highest impact load";
    if (index === total - 1) return "Lowest impact load";
    return "Consistent impact load";
  };
  
  // Calculate the chart-ready data from the tags
  // Calculate the chart-ready data from the tags, factoring in the selected sprint
  // 🔥 UPDATED: Read directly from the Aggregator/DB instead of looping tags
  const defectPhaseData = useMemo(() => {
    const countsMap = {};

    if (selectedSprint === "all") {
      Object.values(sprintBurndownData).forEach(sprint => {
        Object.entries(sprint.defectsByPhase || {}).forEach(([phase, count]) => {
          countsMap[phase] = (countsMap[phase] || 0) + count;
        });
      });
    } else {
      const phases = sprintBurndownData[selectedSprint]?.defectsByPhase || {};
      Object.entries(phases).forEach(([phase, count]) => {
        countsMap[phase] = (countsMap[phase] || 0) + count;
      });
    }

    return Object.entries(countsMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [selectedSprint, sprintBurndownData]);

  const defectPhaseData_o = useMemo(() => {
    const countsMap = {};

    if (selectedSprint === "all") {
      Object.values(sprintBurndownData).forEach(sprint => {
        Object.entries(sprint.defectsByLevel || {}).forEach(([level, count]) => {
          countsMap[level] = (countsMap[level] || 0) + count;
        });
      });
    } else {
      const levels = sprintBurndownData[selectedSprint]?.defectsByLevel || {};
      Object.entries(levels).forEach(([level, count]) => {
        countsMap[level] = (countsMap[level] || 0) + count;
      });
    }

    return Object.entries(countsMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [selectedSprint, sprintBurndownData]);;

// 🔥 NEW: Extract Bug Statuses for the new Pie Chart
const defectStatusData = useMemo(() => {
  const countsMap = {};

  if (selectedSprint === "all") {
    // Loop through all sprints and sum up the statuses
    Object.values(sprintBurndownData).forEach(sprint => {
      const statuses = sprint.bugStatuses || {};
      Object.entries(statuses).forEach(([status, count]) => {
        countsMap[status] = (countsMap[status] || 0) + count;
      });
    });
  } else {
    // Grab statuses just for the selected sprint
    const statuses = sprintBurndownData[selectedSprint]?.bugStatuses || {};
    Object.entries(statuses).forEach(([status, count]) => {
      countsMap[status] = (countsMap[status] || 0) + count;
    });
  }

  // Format for DeveloperPieChart and sort highest to lowest
  return Object.entries(countsMap)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
    
}, [selectedSprint, sprintBurndownData]);

// 🔥 NEW: Extract Bug Priorities for the new Pie Chart
const defectPriorityData = useMemo(() => {
  const countsMap = {};

  if (selectedSprint === "all") {
    Object.values(sprintBurndownData).forEach(sprint => {
      const priorities = sprint.bugPriorityCounts || {};
      Object.entries(priorities).forEach(([priority, count]) => {
        countsMap[priority] = (countsMap[priority] || 0) + count;
      });
    });
  } else {
    const priorities = sprintBurndownData[selectedSprint]?.bugPriorityCounts || {};
    Object.entries(priorities).forEach(([priority, count]) => {
      countsMap[priority] = (countsMap[priority] || 0) + count;
    });
  }

  return Object.entries(countsMap)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
    
}, [selectedSprint, sprintBurndownData]);
  
  // Extract and format data for the Line Graph
const sprintTrendData = useMemo(() => {
  if (!sprintBurndownData) return [];

  // Convert the dictionary into an array
  return Object.entries(sprintBurndownData).map(([sprintName, data]) => ({
    name: sprintName,
    // assigned: data.totalItems || 0,
    completed: data.completedItems || 0,
    velocity: data.Developer_vel || 0,
    // Optional: add a timestamp if you want to explicitly sort by date
    sortDate: data.startDate ? new Date(data.startDate).getTime() : 0 
  }))
  // Sort chronologically from oldest sprint to newest sprint
  .sort((a, b) => a.sortDate - b.sortDate); 

}, [sprintBurndownData]);
  // 🔥 IMPORTANT: Add selectedSprint to the dependency array!

  const formatDecimalToHHMM = (decimalHours) => {
  if (!decimalHours || isNaN(decimalHours)) return "00:00";

  // Grab the whole hours
  const hours = Math.floor(decimalHours);
  
  // Calculate the minutes from the decimal remainder
  const minutes = Math.round((decimalHours - hours) * 60);

  // Pad the minutes with a leading zero if it's less than 10 (e.g., "05")
  const paddedMinutes = minutes < 10 ? `0${minutes}` : minutes;

  return `${hours}:${paddedMinutes}`;
};

  return (
    <main className="snapshot-page">

      {/* 🌟 LEADERBOARD STYLES INJECTED HERE 🌟 */}
      {/* 🌟 LEADERBOARD STYLES INJECTED HERE 🌟 */}
      <style>
        {`
          .dev-leaderboard-card {
            grid-area: leaderboard;
            align-self: stretch; /* Fill vertical space to match neighboring cards */
            display: flex;
            flex-direction: column;
            background: linear-gradient(145deg, rgba(18, 40, 56, 0.94), rgba(12, 24, 36, 0.9)), linear-gradient(180deg, rgba(255, 255, 255, 0.04), transparent);
            border: 2px solid rgba(159, 223, 255, 0.28);
            border-radius: 8px; /* Matched to sprint card */
            margin: 0;
            box-shadow: var(--shadow-md);
            backdrop-filter: blur(16px);
          }
          
          /* New container to separate header and give it breathing room */
          .dev-leaderboard-header-section {
            padding: 18px 20px;
            border-bottom: 1px solid rgba(159, 223, 255, 0.14);
          }
          .dev-leaderboard-header { 
            display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; 
          }
          .dev-leaderboard-header h2 { 
            margin: 0; font-size: 1.1rem; color: var(--ink-strong); font-weight: 700; 
          }
          .dev-leaderboard-badge { 
            background-color: rgba(159, 223, 255, 0.12); color: var(--accent-teal); font-size: 0.7rem; padding: 4px 10px; border-radius: 20px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; 
          }
          .dev-leaderboard-subtext { 
            color: var(--ink-soft); font-size: 0.85rem; margin: 0; font-weight: 400; 
          }
          
          /* The table wrapper */
          .dev-leaderboard-body {
            padding: 20px 10px; /* Reduced side padding to give the table more room */
            flex: 1;
            overflow-y: auto;
            overflow-x: auto; /* Failsafe horizontal scroll */
          }
          
          .dev-leaderboard-table { width: 100%; border-collapse: collapse; }
          
          /* FIX: Tighter padding inside headers */
          .dev-leaderboard-table th { 
            text-align: left; 
            color: var(--ink-muted); 
            font-size: 0.72rem; 
            font-weight: 700; 
            letter-spacing: 0.05em; 
            padding: 0 10px 16px 10px; 
            border-bottom: 1px solid rgba(159, 223, 255, 0.14); 
            text-transform: uppercase; 
          }
          .dev-leaderboard-table th:first-child { padding-left: 0; }
          .dev-leaderboard-table th:last-child { padding-right: 0; }

          .dev-leaderboard-row { border-bottom: 1px solid rgba(159, 223, 255, 0.08); transition: background-color 0.2s ease; }
          .dev-leaderboard-row:hover { background-color: rgba(86, 199, 221, 0.06); }
          .dev-leaderboard-row:last-child { border-bottom: none; }
          
          /* FIX: Tighter padding inside data cells */
          .dev-leaderboard-cell { 
            padding: 14px 10px; 
            vertical-align: middle; 
          }
          .dev-leaderboard-row td:first-child { padding-left: 0; }
          .dev-leaderboard-row td:last-child { padding-right: 0; }

          .dev-rank-circle { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 0.9rem; color: #0f172a; }
          .dev-avatar-circle { width: 40px; height: 40px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 1.1rem; color: #0f172a; margin-right: 16px; }
          .dev-name-container { display: flex; align-items: center; }
          .dev-name-text { display: flex; flex-direction: column; }
          .dev-name-title { font-weight: 600; color: var(--ink-strong); font-size: 0.95rem; white-space: nowrap; }
          .dev-bugs-pill { background-color: rgba(159, 223, 255, 0.08); color: var(--ink-strong); padding: 4px 12px; border-radius: 20px; font-weight: 600; font-size: 0.85rem; display: inline-block; min-width: 45px; text-align: center; }
        `}
      </style>


      <div className="snapshot-toolbar">
        <button type="button" className="snapshot-back-button" onClick={() => navigate("/")}>
          Back to Landing
        </button>

        <section className="snapshot-control-panel">
          <Filters onSelect={handleSelect} isSubmitting={isLoading} />
        </section>
      </div>

      <section className="snapshot-canvas" aria-label="Project status dashboard">

        {/* --- COLUMN 1: LEFT (Compact Data & Tables) --- */}
        <div className="snapshot-col">
          <div className="snapshot-title-box">{projectName}</div>

          <div className="snapshot-summary-grid">
            <div className="snapshot-outline-box">
              <span>Project Start Date</span>
              <strong>{formatDate(projectBurndownData.startDate)}</strong>
            </div>
            <div className="snapshot-outline-box">
              <span>Project End Date</span>
              <strong>{formatDate(projectBurndownData.endDate)}</strong>
            </div>
            <div className="snapshot-outline-box">
              <span>Project Status</span>
              <strong>{projectStatus}</strong>
            </div>
            <div className="snapshot-outline-box">
              <span>Project Completion %</span>
              <strong>{ sprintStats.projectCompletionPercentage !== null ? `${sprintStats.projectCompletionPercentage}%` : "N/A"}</strong>
            </div>
          </div>

          <section className="snapshot-table-card snapshot-milestones">
            <h2>Upcoming Milestones</h2>
            <div className="snapshot-table">
              <div className="snapshot-table-head">Milestones</div>
              <div className="snapshot-table-head">Status</div>
              <div className="snapshot-table-head">Finish Date</div>
              <div className="snapshot-table-head">Baseline Finish</div>
              <div className="snapshot-table-head">Days Late</div>

              {milestones.length ? (
                milestones.map((milestone) => (
                  <React.Fragment key={milestone.name}>
                    <div>{milestone.name}</div>
                    <div>{milestone.status}</div>
                    <div>{formatDate(milestone.finishDate)}</div>
                    <div>{formatDate(milestone.baselineFinish)}</div>
                    <div>{milestone.daysLate}</div>
                  </React.Fragment>
                ))
              ) : (
                <div className="snapshot-table-empty">Select a project to load milestone data.</div>
              )}
            </div>
          </section>

          <section className="snapshot-sprint-card">
            <h2>Sprint Management Dashboard</h2>
            <div className="snapshot-sprint-grid">
              <div className="snapshot-sprint-top">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <label style={{ margin: 0 }}>Sprint</label>
                </div>
                <SearchableSprintDropdown sprintNames={sprintNames} selectedSprint={selectedSprint} onSelect={setSelectedSprint} />
              </div>

              <div className="snapshot-sprint-top">
                <span>Total Team Efforts</span>
                <strong>{sprintStats.totalLoggedHours > 0 ? formatDecimalToHHMM(sprintStats.totalLoggedHours) : "00:00"}</strong>
              </div>

              <div className="snapshot-variance-box">
                <strong>{sprintStats.taskVariance}%</strong>
                <span>Task Delivery Variance</span>
                <div>
                  <b>{sprintStats.completedItems}</b><em>Completed</em><i />
                  <b>{sprintStats.assignedItems}</b><em>Assigned</em>
                </div>
              </div>

              <div className="snapshot-variance-box">
                <strong>{sprintStats.pointVariance}%</strong>
                <span>Story Points Variance</span>
                <div>
                  <b>{sprintStats.completedPoints}</b><em>Completed</em><i />
                  <b>{sprintStats.totalPoints}</b><em>Assigned</em>
                </div>
              </div>

              <div className="snapshot-velocity-wrapper">
                <div className="velocity-item">
                  <span>Developer Velocity</span><strong>{sprintStats.devVelocity || "0"}</strong>
                </div>
                <div className="velocity-item">
                  <span>Delivery Velocity</span><strong>{sprintStats.deliveryVelocity || "0"}</strong>
                </div>
              </div>
            </div>
          </section>

          {/* 🔥 NEW: SPRINT GOAL CARD (Moved from Modal) */}
          {selectedSprint !== "all" && sprintStats.description && (
            <section className="snapshot-defects-card" style={{ minHeight: "auto" }}>
              <h2>Sprint Goal / Description</h2>
              <div 
                style={{ padding: "16px 20px", color: "var(--ink-soft)", fontSize: "0.95rem", lineHeight: "1.6" }}
                dangerouslySetInnerHTML={{ __html: sprintStats.description }}
              />
            </section>
          )}

          <section className="dev-leaderboard-card">
            <div className="dev-leaderboard-header-section">
              <div className="dev-leaderboard-header">
                <h2>Developer Leaderboard</h2>
                <div className="dev-leaderboard-badge">{devLeaderboard.length} DEVELOPERS</div>
              </div>
              <div className="dev-leaderboard-subtext">Item counts and relative sprint impact</div>
            </div>

            <div className="dev-leaderboard-body">
              {devLeaderboard.length > 0 ? (
                <table className="dev-leaderboard-table">
                  <thead>
                    <tr>
                      <th>DEVELOPER</th>
                      <th style={{ textAlign: "left" }}>Assigned</th>
                      <th style={{ textAlign: "center" }}>COMPLETED</th>
                      <th style={{ textAlign: "right" }}>
                        <Tooltip text="Story points or tasks that were not finished in the current sprint and moved over to the next iteration." position="bottom">
                          <span style={{ cursor: "help" }}>CARRIED OVER</span>
                        </Tooltip>
                      </th>
                      <th style={{ textAlign: "right" }}>COMPLETION %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devLeaderboard.map((dev, index) => {
                      const isUnassigned = dev.name === "Unassigned";
                      const rankColor = isUnassigned ? "#334155" : (rankColors[index] || "#475569"); 
                      const avatarColor = isUnassigned ? "#1e293b" : avatarColors[dev.name.length % avatarColors.length];
                      const avatarChar = isUnassigned ? "?" : dev.name.charAt(0).toUpperCase();
                      
                      return (
                        <tr key={dev.name} className="dev-leaderboard-row">
                          <td className="dev-leaderboard-cell">
                            <div className="dev-name-container">
                              <div className="dev-avatar-circle" style={{ backgroundColor: avatarColor, color: isUnassigned ? "#64748b" : "#0f172a" }}>{avatarChar}</div>
                              <div className="dev-name-text">
                                <span className="dev-name-title" style={{ color: isUnassigned ? "#94a3b8" : "#f8fafc" }}>{dev.name}</span>
                              </div>
                            </div>
                          </td>
                          <td className="dev-leaderboard-cell" style={{ textAlign: "center" }}>
                            <div className="dev-bugs-pill" style={{ backgroundColor: isUnassigned ? "transparent" : "rgba(159, 223, 255, 0.08)", color: isUnassigned ? "#64748b" : "var(--ink-strong)", border: isUnassigned ? "1px solid #334155" : "none" }}>
                              {dev.assigned}
                            </div>
                          </td>
                          <td className="dev-leaderboard-cell" style={{ textAlign: "center", color: isUnassigned ? "#64748b" : "var(--ink-strong)", fontWeight: "500" }}>{dev.completed}</td>
                          <td className="dev-leaderboard-cell" style={{ textAlign: "center", color: "var(--ink-soft)", fontWeight: "500" }}>{dev.moved}</td>
                          <td className="dev-leaderboard-cell" style={{ textAlign: "right", color: "var(--ink-soft)", fontWeight: "500" }}>
                            {dev.assigned > 0 ? `${Math.round((dev.completed / dev.assigned) * 100)}%` : "0%"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div style={{ color: "var(--ink-soft)", padding: "20px 0" }}>No developer item data available for this selection.</div>
              )}
            </div>
          </section>
        </div>

        {/* --- COLUMN 2: MIDDLE (Trend & Detection Pies) --- */}
        <div className="snapshot-col">
          <section className="snapshot-defects-card">
            <h2>Sprint-Over-Sprint Trend</h2>
            <div style={{ flex: 1, padding: "20px 10px 10px" }}>
              <SprintTrendChart data={sprintTrendData} />
            </div>
          </section>

          <section className="snapshot-defects-card snapshot-defects-phase-card">
            <h2>Defects by Detection Phase</h2>
            {/* 👇 Added overflowY and overflowX to the wrapper */}
            <div style={{ flex: 1, minHeight: 0, padding: "10px", overflowY: "auto", overflowX: "hidden" }}>
              <DeveloperPieChart data={defectPhaseData} label="Bugs" disableSort={false} />
            </div>
          </section>

          <section className="snapshot-defects-card snapshot-defects-level-card">
            <h2>Defects by Detection Level</h2>
            <div style={{ flex: 1, minHeight: 0, padding: "10px", overflowY: "auto", overflowX: "hidden" }}>
              <DeveloperPieChart data={defectPhaseData_o} label="Bugs" disableSort={false} />
            </div>
          </section>
        </div>

        {/* --- COLUMN 3: RIGHT (Vertically Centered Status Pie) --- */}
        <div className="snapshot-col" style={{ justifyContent: "center" }}>
          <section className="snapshot-defects-card snapshot-defects-status-card">
            <h2>Defects by End Status</h2>
            <div style={{ flex: 1, minHeight: 0, padding: "10px", overflowY: "auto", overflowX: "hidden" }}>
              <DeveloperPieChart data={defectStatusData} label="Bugs" disableSort={false} />
            </div>
          </section>

          {/* 🔥 NEW: Priority Card */}
          <section className="snapshot-defects-card">
            <h2>Defects by Priority</h2>
            <div style={{ flex: 1, minHeight: 0, padding: "10px", overflowY: "auto", overflowX: "hidden" }}>
              <DeveloperPieChart data={defectPriorityData} label="Bugs" disableSort={false} />
            </div>
          </section>
        </div>

      </section>

      {/* 🔥 THE MODAL (With Cross Mark) */}
      {isDescModalOpen && (
        <div className="sprint-desc-modal-overlay" onClick={() => setIsDescModalOpen(false)}>
          <div className="sprint-desc-modal-content" onClick={(e) => e.stopPropagation()}>
            
            <div className="sprint-desc-modal-header">
              <h3>Sprint Goal / Description</h3>
              {/* ❌ The Cross Mark to close */}
              <button className="close-modal-btn" onClick={() => setIsDescModalOpen(false)}>
                &times; 
              </button>
            </div>
            
            <div 
              className="sprint-desc-modal-body"
              dangerouslySetInnerHTML={{ __html: sprintStats.description }}
            />
            
          </div>
        </div>
      )}


    </main>
  );
}

export default ProjectSnapshot;
